'use client'

import { type KeyboardEvent, useRef } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'

import { PriceLadderTile } from './price-ladder-tile'
import type { PriceLevelTab } from './price-section-types'

interface PriceLadderProps {
  /** Display order: Premium first, base tier last. */
  levels: PriceLevelTab[]
  selectedLevelId: number
  onSelect: (levelId: number) => void
}

/**
 * The tier ladder: one radio tile per technology level in a single row on
 * desktop, two columns on phones. Arrow keys move the selection the way a
 * native radio group does, so selection follows focus.
 */
export const PriceLadder = ({ levels, selectedLevelId, onSelect }: PriceLadderProps) => {
  const { t } = useTranslation()
  const tileRefs = useRef<(HTMLButtonElement | null)[]>([])

  const moveSelection = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (index + 1) % levels.length
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (index - 1 + levels.length) % levels.length
    } else if (event.key === 'Home') {
      next = 0
    } else if (event.key === 'End') {
      next = levels.length - 1
    }
    if (next === null) {
      return
    }
    event.preventDefault()
    onSelect(levels[next].id)
    tileRefs.current[next]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('client.product.selectTechnologyLevel', 'Select technology level')}
      data-count={levels.length}
      className="grid grid-cols-2 gap-2.5 sm:grid-flow-col sm:auto-cols-fr sm:grid-cols-none"
    >
      {levels.map((level, index) => {
        const selected = level.id === selectedLevelId
        return (
          <PriceLadderTile
            key={level.id}
            level={level}
            selected={selected}
            onSelect={() => onSelect(level.id)}
            onKeyDown={(event) => moveSelection(event, index)}
            buttonRef={(element) => {
              tileRefs.current[index] = element
            }}
          />
        )
      })}
    </div>
  )
}
